import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { meApi, type BootTokenCreated } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { useFeature } from '@/hooks/useFeature';
import AddNodeWizard from './AddNodeWizard';
import PairWaitingServerModal from './PairWaitingServerModal';

// boot.jeket.com is the single production hostname for the JSS deployment
// (see CLAUDE.md). When/if multi-environment lands we'll drive this from
// a Vite env var.
const BOOT_SERVICE_BASE = 'https://boot.jeket.com';

function NetworkBootCardBody({ siteId }: { siteId: string }) {
  const { data: org, error: orgError, isLoading: orgLoading } = useQuery({
    queryKey: ['me', 'organization'],
    queryFn: () => meApi.organization(),
    staleTime: 60_000,
  });
  const slug = org?.slug;

  // `/api/me/organization` 404s for an admin/superuser whose Keycloak token
  // carries no `org_id` claim, unless they've picked an org in the header
  // switcher (which sets X-Org-Override). That's the normal state for the
  // platform owner, and it used to render as a permanent "Loading…" over
  // three dead buttons with nothing saying why. Name the condition instead.
  const noOrg = !!orgError && (orgError as Error).message.startsWith('API error 404');

  const [copied, setCopied] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [pairModalOpen, setPairModalOpen] = useState(false);
  // Phase 5d — when the operator pairs a waiting server, the modal hands
  // us the freshly-minted token. We then open AddNodeWizard with that
  // token as `preMintedToken`, which jumps straight to Live progress.
  const [pairedToken, setPairedToken] = useState<BootTokenCreated | null>(null);

  const biosUrl = slug ? `${BOOT_SERVICE_BASE}/b/${slug}/ipxe.efi` : null;

  const onCopy = async () => {
    if (!biosUrl) return;
    try {
      await navigator.clipboard.writeText(biosUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-silver-muted">
        HTTPS UEFI HTTP Boot — configure your servers&apos; BIOS once with the URL
        below, then mint a one-shot per-node boot URL when you&apos;re ready to
        provision a machine.
      </p>

      {/* Fixed BIOS HTTP Boot URL (per-org, no token) */}
      <div className="bg-surface-deep rounded-lg p-3 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-silver-muted">
          BIOS HTTP Boot URL
        </div>
        <div className="font-mono text-xs text-silver-bright break-all">
          {biosUrl ?? (
            <span className="text-silver-muted">
              {orgLoading
                ? 'Loading…'
                : noOrg
                  ? 'No organization selected'
                  : 'Unavailable'}
            </span>
          )}
        </div>
        {!biosUrl && !orgLoading && (
          <p className="text-[10px] text-amber-400/90">
            {noOrg
              ? 'This account has no organization on its token. Pick one from the account menu in the header to scope the boot URL.'
              : `Could not resolve your organization: ${(orgError as Error | null)?.message ?? 'unknown error'}`}
          </p>
        )}
        <button
          type="button"
          onClick={onCopy}
          disabled={!biosUrl}
          className="inline-flex items-center gap-1.5 text-[11px] text-silver-muted hover:text-silver-bright transition-colors disabled:opacity-40"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copied' : 'Copy URL'}
        </button>
      </div>

      {/* A minted boot URL, or a paired server, gets the site's join key and
          adds a box — gated like the USB image download (JSS#883). */}
      <RoleGate
        role={Role.SiteOperator}
        fallback={<p className="text-[10px] text-silver-muted">Adding boxes needs the Site Operator role.</p>}
      >
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              setPairedToken(null);
              setPairModalOpen(true);
            }}
            disabled={!slug}
            className="w-full py-2 text-sm font-medium rounded bg-gold text-surface-deep hover:bg-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Pair waiting server
          </button>
          <button
            type="button"
            onClick={() => {
              setPairedToken(null);
              setWizardOpen(true);
            }}
            disabled={!slug}
            className="w-full py-2 text-sm font-medium rounded border border-surface-elevated text-silver-bright hover:bg-surface-elevated transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Mint per-node boot URL
          </button>
        </div>
      </RoleGate>

      <PairWaitingServerModal
        open={pairModalOpen}
        siteId={siteId}
        onClose={() => setPairModalOpen(false)}
        onPaired={(token) => {
          setPairModalOpen(false);
          setPairedToken(token);
          setWizardOpen(true);
        }}
      />

      <AddNodeWizard
        open={wizardOpen}
        siteId={siteId}
        onClose={() => {
          setWizardOpen(false);
          setPairedToken(null);
        }}
        preMintedToken={pairedToken}
      />
    </div>
  );
}

/**
 * The site Overview's Network Boot card (Phase 5a/b): the per-org BIOS HTTP
 * Boot URL, "Pair waiting server" and "Mint per-node boot URL".
 *
 * Behind the `netboot` feature flag, off by default (#954). Netboot has not
 * booted a real machine yet, so customers install from the USB image (Boot
 * Images card). Nothing here renders, or calls boot-service, until the flag
 * is on for this site.
 */
export default function NetworkBootCard({ siteId }: { siteId: string }) {
  const netboot = useFeature('netboot', { siteId });
  if (!netboot) return null;
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5">
      <h3 className="text-sm font-semibold text-silver-bright mb-4">Network Boot</h3>
      <NetworkBootCardBody siteId={siteId} />
    </div>
  );
}
